/**
 * Locations Modal JavaScript
 * Handles zip code lookup and redirect functionality
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    dataUrl: null, // Will be set from asset_url
    notFoundPage: '/pages/location-not-found',
    locationPageBase: '/pages/'
  };

  // State
  let locationsData = null;
  let isLoading = false;

  // DOM Elements
  let modal = null;
  let form = null;
  let input = null;
  let submitBtn = null;
  let errorEl = null;

  /**
   * Initialize the modal
   */
  function init() {
    modal = document.getElementById('locations-modal');
    if (!modal) {
      console.warn('Locations modal element not found');
      return;
    }

    form = document.getElementById('locations-modal-form');
    input = document.getElementById('locations-modal-zip');
    submitBtn = document.getElementById('locations-modal-submit');
    errorEl = document.getElementById('locations-modal-error');

    // Find the data URL from the script tag or use default
    const scriptTag = document.querySelector('script[src*="locations-modal.js"]');
    if (scriptTag) {
      const srcUrl = new URL(scriptTag.src);
      CONFIG.dataUrl = srcUrl.origin + srcUrl.pathname.replace('locations-modal.js', 'locations-data.json');
    } else {
      // Fallback - try to construct from current page
      CONFIG.dataUrl = '/cdn/shop/t/1/assets/locations-data.json';
    }

    // Bind events
    bindEvents();

    // Preload data
    loadLocationsData();

    // Expose global function
    window.openLocationsModal = openModal;

    console.log('Locations modal initialized');
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Close buttons
    const closeButtons = modal.querySelectorAll('[data-locations-modal-close]');
    closeButtons.forEach(function(btn) {
      btn.addEventListener('click', closeModal);
    });

    // Form submit
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }

    // Input validation on change
    if (input) {
      input.addEventListener('input', function() {
        hideError();
        // Only allow numbers
        this.value = this.value.replace(/\D/g, '');
      });

      input.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit(e);
        }
      });
    }

    // ESC key to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        closeModal();
      }
    });

    // Global click handler for trigger links/buttons
    document.addEventListener('click', function(e) {
      const target = e.target.closest('a, button');
      if (target) {
        const href = target.getAttribute('href');
        const dataAttr = target.getAttribute('data-open-locations-modal');
        if (href === '#locations-modal' || href === '#find-location' || dataAttr !== null) {
          e.preventDefault();
          openModal();
        }
      }
    });
  }

  /**
   * Load locations data JSON
   */
  function loadLocationsData() {
    if (locationsData || isLoading) return;

    isLoading = true;

    fetch(CONFIG.dataUrl)
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to load locations data');
        }
        return response.json();
      })
      .then(function(data) {
        locationsData = data;
        isLoading = false;
        console.log('Locations data loaded:', Object.keys(data.zipToSlug).length, 'zip codes');
      })
      .catch(function(error) {
        console.error('Error loading locations data:', error);
        isLoading = false;
      });
  }

  /**
   * Open the modal
   */
  function openModal() {
    if (!modal) return;

    modal.classList.add('active');
    document.body.classList.add('locations-modal-open');

    // Focus input after animation
    setTimeout(function() {
      if (input) {
        input.value = '';
        input.focus();
      }
    }, 100);

    // Ensure data is loaded
    loadLocationsData();

    // Add focus trap
    modal.addEventListener('keydown', trapFocus);
  }

  /**
   * Close the modal
   */
  function closeModal() {
    if (!modal) return;

    modal.classList.remove('active');
    document.body.classList.remove('locations-modal-open');
    hideError();

    // Remove focus trap
    modal.removeEventListener('keydown', trapFocus);
  }

  /**
   * Handle form submission
   */
  function handleSubmit(e) {
    e.preventDefault();

    const zipcode = input ? input.value.trim() : '';

    // Validate zip code format
    if (!zipcode || !/^\d{5}$/.test(zipcode)) {
      showError();
      return;
    }

    // Check if data is loaded
    if (!locationsData) {
      console.error('Locations data not loaded yet');
      // Try to load and retry
      loadLocationsData();
      setTimeout(function() {
        if (locationsData) {
          performSearch(zipcode);
        } else {
          showError('Unable to load location data. Please try again.');
        }
      }, 1000);
      return;
    }

    performSearch(zipcode);
  }

  /**
   * Perform the zip code search and redirect
   */
  function performSearch(zipcode) {
    const slug = locationsData.zipToSlug[zipcode];

    if (slug) {
      // Found - redirect to location page
      window.location.href = CONFIG.locationPageBase + slug;
    } else {
      // Not found - redirect to not found page with zip param
      window.location.href = CONFIG.notFoundPage + '?zip=' + encodeURIComponent(zipcode);
    }
  }

  /**
   * Show error message
   */
  function showError(message) {
    if (errorEl) {
      if (message) {
        errorEl.textContent = message;
      }
      errorEl.classList.add('visible');
    }
    if (input) {
      input.focus();
      input.select();
    }
  }

  /**
   * Hide error message
   */
  function hideError() {
    if (errorEl) {
      errorEl.classList.remove('visible');
    }
  }

  /**
   * Trap focus within modal
   */
  function trapFocus(e) {
    if (e.key !== 'Tab') return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      lastElement.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      firstElement.focus();
      e.preventDefault();
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
